import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Portfolio,
  CreatePortfolioRequest,
  portfolioApi,
} from "../../services/portfolioApi";
import { queryKeys } from "./keys";

/**
 * Fetch the list of portfolios for the current user.
 */
export function usePortfolios() {
  return useQuery({
    queryKey: queryKeys.portfolios.all,
    queryFn: async () => {
      const response = await portfolioApi.getPortfolios();
      return response.results as Portfolio[];
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch a single portfolio by ID (including items summary).
 */
export function usePortfolioDetail(portfolioId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.portfolios.detail(portfolioId!),
    queryFn: () => portfolioApi.getPortfolio(portfolioId!),
    enabled: !!portfolioId,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch portfolio items with optional filters.
 */
export function usePortfolioItems(
  portfolioId: string | undefined,
  filters?: Record<string, unknown>,
) {
  return useQuery({
    queryKey: queryKeys.portfolios.items(portfolioId!, filters),
    queryFn: () =>
      portfolioApi.getPortfolioItems(portfolioId!, filters as any),
    enabled: !!portfolioId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch portfolio summary (entity counts for tab badges).
 */
export function usePortfolioSummary(portfolioId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.portfolios.summary(portfolioId!),
    queryFn: () => portfolioApi.getPortfolioSummary(portfolioId!),
    enabled: !!portfolioId,
    staleTime: 30 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────

export function useCreatePortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePortfolioRequest) =>
      portfolioApi.createPortfolio(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portfolios.all });
    },
  });
}

export function useUpdatePortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreatePortfolioRequest>;
    }) => portfolioApi.updatePortfolio(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portfolios.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portfolios.detail(variables.id),
      });
    },
  });
}

export function useDeletePortfolio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => portfolioApi.deletePortfolio(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portfolios.all });
    },
  });
}
